import React, { useState, useMemo, useEffect } from 'react';
import { X, Folder, Calendar, Trash2, Plus, Radio, Search } from 'lucide-react';
import { useSiteStore } from '../../store/siteStore';

interface SiteListSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateNew: () => void;
}

const SiteListSidebar: React.FC<SiteListSidebarProps> = ({ isOpen, onClose, onCreateNew }) => {
  const [topOffset, setTopOffset] = useState(64);
  
  useEffect(() => {
    const calculateTopOffset = () => {
      const header = document.querySelector('header');
      
      if (header) {
        const headerHeight = header.getBoundingClientRect().height;
        setTopOffset(headerHeight);
      }
    };
    
    calculateTopOffset();
    window.addEventListener('resize', calculateTopOffset);
    
    return () => {
      window.removeEventListener('resize', calculateTopOffset);
    };
  }, []);

  const { 
    availableSites, 
    currentSite, 
    loadSite, 
    deleteSite,
    refreshSites 
  } = useSiteStore();

  const [searchQuery, setSearchQuery] = useState('');

  React.useEffect(() => {
    if (isOpen) {
      refreshSites();
    }
  }, [isOpen, refreshSites]);

  // Filter sites based on search query
  const filteredSites = useMemo(() => {
    if (!searchQuery.trim()) {
      return availableSites;
    }
    const query = searchQuery.toLowerCase();
    return availableSites.filter(site => 
      site.name.toLowerCase().includes(query) ||
      site.location.toLowerCase().includes(query)
    );
  }, [availableSites, searchQuery]);

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const handleDeleteSite = (siteId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this site? This action cannot be undone.')) {
      deleteSite(siteId);
    }
  };

  const handleLoadSite = (siteId: string) => {
    loadSite(siteId);
    onClose();
  };

  return (
    <div 
      className={`fixed left-12 bottom-0 w-80 bg-slate-900 text-white border-r border-slate-700 flex flex-col transition-transform duration-300 ease-in-out z-50 ${
        isOpen ? 'translate-x-0' : '-translate-x-full'
      }`}
      style={{ top: `${topOffset}px` }}
    >
      <div className="p-4 border-b border-slate-700 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-blue-400 flex items-center">
          <Radio size={18} className="mr-2" />
          Sites
        </h2>
        <button
          onClick={onClose}
          className="p-1 hover:bg-slate-800 rounded transition-colors"
        >
          <X size={18} className="text-gray-400" />
        </button>
      </div>
      
      <div className="flex-1 overflow-y-auto custom-scrollbar p-4">
        {/* Search Bar */}
        {availableSites.length > 0 && (
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
            <input
              type="text"
              placeholder="Search sites..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-slate-800 border border-slate-600 rounded-md text-sm text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            />
          </div>
        )}

        <button
          onClick={onCreateNew}
          className="w-full flex items-center justify-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md transition-colors mb-4"
        >
          <Plus size={16} />
          <span>New Site</span>
        </button>

        {availableSites.length === 0 ? (
          <div className="text-center py-12">
            <Folder size={48} className="mx-auto text-gray-500 mb-4" />
            <h3 className="text-lg font-medium text-gray-300 mb-2">No Sites Found</h3>
            <p className="text-gray-400 mb-4">Create your first tower site to get started</p>
            <button
              onClick={onCreateNew}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-md transition-colors"
            >
              Create New Site
            </button>
          </div>
        ) : filteredSites.length === 0 ? (
          <div className="text-center py-8">
            <Search size={32} className="mx-auto text-gray-500 mb-2" />
            <p className="text-sm text-gray-400">No sites found matching "{searchQuery}"</p>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredSites.map((site) => (
              <div
                key={site.id}
                onClick={() => handleLoadSite(site.id)}
                className={`bg-slate-800 rounded-lg p-3 cursor-pointer transition-all hover:bg-slate-700 border-2 group ${
                  currentSite?.id === site.id 
                    ? 'border-blue-500 bg-blue-900/30' 
                    : 'border-transparent'
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center space-x-2 flex-1 min-w-0">
                    <Radio className="text-blue-400 flex-shrink-0" size={16} />
                    <h3 className="font-semibold text-white truncate">{site.name}</h3>
                  </div>
                  <button
                    onClick={(e) => handleDeleteSite(site.id, e)}
                    className="p-1 hover:bg-red-600 rounded transition-colors flex-shrink-0 opacity-0 group-hover:opacity-100"
                    title="Delete Site"
                  >
                    <Trash2 size={14} className="text-red-400" />
                  </button>
                </div>
                
                <div className="space-y-1 text-xs text-gray-400">
                  <div className="truncate">{site.location}</div>
                  <div className="flex items-center space-x-3">
                    <span>{site.equipment.length} equipment</span>
                    <span>{site.tower.height}m tower</span>
                  </div>
                  <div className="flex items-center space-x-1 text-gray-500">
                    <Calendar size={10} />
                    <span>Updated {formatDate(site.updatedAt)}</span>
                  </div>
                </div>
                
                {currentSite?.id === site.id && (
                  <div className="mt-2 text-xs bg-blue-600 text-white px-2 py-1 rounded">
                    Currently Open
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SiteListSidebar;

